import { useState, useEffect } from 'react'
import axios from 'axios'
import { BASE_URL } from '../apiConfig'
import Cookies from 'js-cookie'
import { History, X, User, Calendar, TrendingUp, ShoppingCart, Info } from 'lucide-react'
import { useTranslation } from '../LanguageContext'
import Loading from '../element/Loading'

export default function ProductHistoryModal({ product, onClose }) {
  const { t, lang } = useTranslation()
  const token = Cookies.get('WEB_SSR')

  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const typeMeta = {
    created: {
      label: t('products.history.initial'),
      classes: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
      iconClass: 'text-green-600 dark:text-green-400',
      icon: Info,
    },
    augmented: {
      label: t('products.history.augmented'),
      classes: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
      iconClass: 'text-teal-600 dark:text-teal-400',
      icon: TrendingUp,
    },
    invoice: {
      label: t('products.history.sale'),
      classes: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
      iconClass: 'text-purple-600 dark:text-purple-400',
      icon: ShoppingCart,
    },
  }

  const fetchHistory = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${BASE_URL}/api/products/${product.id}/history`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      })
      if (response.data.success) {
        setHistory(response.data.data.history || [])
        setError(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load history')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchHistory() }, [product.id])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl dark:bg-gray-800" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
              <History className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{t('products.history.title')}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{product.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <Loading size={10} />
            </div>
          ) : error ? (
            <p className="py-10 text-center text-sm text-red-500">{error}</p>
          ) : history.length === 0 ? (
            <p className="py-10 text-center text-gray-500 dark:text-gray-400">{t('products.history.no_data')}</p>
          ) : (
            <div className="relative space-y-3 pl-4">
              <div className="absolute left-0 top-1 bottom-1 w-px bg-gray-200 dark:bg-gray-700" />
              {history.map((entry, index) => {
                const meta = typeMeta[entry.type] || typeMeta.invoice
                const TypeIcon = meta.icon
                return (
                  <div key={`${entry.type}-${entry.date}-${index}`} className="relative rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/40">
                    <div className="absolute -left-4 top-4 rounded-full border border-gray-200 bg-white p-1 dark:border-gray-600 dark:bg-gray-800">
                      <TypeIcon className={`h-3 w-3 ${meta.iconClass}`} />
                    </div>

                    <div className="flex items-center flex-wrap gap-2">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${meta.classes}`}>
                        {meta.label}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="h-3 w-3" />
                        {new Date(entry.date).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                        })}
                      </span>
                      {entry.user && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <User className="h-3 w-3" />
                          {entry.user}
                        </span>
                      )}
                    </div>

                    <p className="mt-2 text-sm text-gray-900 dark:text-gray-50">{entry.description}</p>

                    {entry.type === 'invoice' && (
                      <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-2 dark:border-purple-800 dark:bg-purple-900/20">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">{entry.invoice_code}</span>
                          <span className="text-xs text-purple-700 dark:text-purple-300">{t('products.history.client')}: {entry.client_name}</span>
                        </div>
                        {entry.lines && entry.lines.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {entry.lines.map((line, li) => (
                              <div key={li} className="flex items-center justify-between text-xs text-purple-800 dark:text-purple-200">
                                <span>{line.reference}</span>
                                <span>{lang == 'ar' ? line.quantity + '-' : '-' + line.quantity} {t('table.pcs')}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="mt-1 border-t border-purple-200 pt-1 text-xs font-semibold text-purple-800 dark:border-purple-800 dark:text-purple-200">
                          {t('products.history.sold')}: {entry.qty_sold} {t('table.pcs')}
                        </p>
                      </div>
                    )}

                    {entry.type === 'created' && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {t('products.history.stock_qty')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{entry.qty}</span> {t('table.pcs')}
                      </p>
                    )}

                    {entry.type === 'augmented' && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {t('activity_log.old_stock')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{entry.old_qty}</span> {lang == 'ar'? "←" : "→"} {t('activity_log.new_stock')}: <span className="font-semibold text-gray-700 dark:text-gray-200">{entry.new_qty}</span> ({t('activity_log.added')}: <span className="font-semibold text-teal-600 dark:text-teal-400">{lang == 'ar' ? entry.added + '+' : '+' + entry.added}</span>) {t('table.pcs')}
                      </p>
                    )}

                    {Array.isArray(entry.variants) && entry.variants.length > 0 && (
                      <div className="mt-2 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-800/60">
                        {entry.variants.map((v, vi) => (
                          <div key={vi} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                            <span className="font-medium text-gray-700 dark:text-gray-200">{v.name}</span>
                            {v.old_stock !== undefined ? (
                              <span className="text-gray-500 dark:text-gray-400">
                                {v.old_stock} {lang == 'ar'? "←" : "→"} {v.new_stock} <span className="font-semibold text-teal-600 dark:text-teal-400">({lang == 'ar' ? v.added + '+' : '+' + v.added})</span> {t('table.pcs')}
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-gray-400">{v.stock} {t('table.pcs')}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {t('invoices.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}